import { useRef, useState } from 'react';
// Third-party
import { FormProvider, useForm } from 'react-hook-form';
import useTranslation from 'next-translate/useTranslation';

import clsx from 'clsx';

// Hooks
import useComments from '@/utils/hooks/swr/useComments';

// Utils
import { get, post as postRequest } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';
import useIntersectionObserver from '@/utils/hooks/utils/useIntersectionObserver';
import useStateWithReset from '@/utils/hooks/utils/useStateWithReset';
import useToggle from '@/utils/hooks/utils/useToggle';

import PropTypes from 'prop-types';

// Endpoints
import { COMMENT_MARK_ALL_AS_READ, COMMENTS_EXPORT } from '@/lib/routes/apiEndpoints';

// Components
import Icon from '@/components/base/Icon';
import InfoBanner from '@/components/base/InfoBanner/CompoundBanner';
// Composites
import { CommentForm } from '@/components/composite/Comments/CommentForm';
import CommentsList from '@/components/composite/Comments/CommentsList';
import ExportXlsModal from '@/components/composite/Modals/ExportXlsModal';
import RandomCommentsPicker from '@/components/composite/RandomCommentsPicker';
import RandomCommentsPickerResults from '@/components/composite/RandomCommentsPicker/RandomCommentsPickerResults';

const MediaViewerComments = ({ post, socialProfile, isLoading }) => {
    const { t } = useTranslation('commons');
    const [mediaViewerCommentsRef, setMediaViewerCommentsRef] = useState(null);
    const [activeFormReply, setActiveFormReply] = useState(null);
    const formMethods = useForm({ mode: 'all', defaultValues: { message: '' } });
    const [markAllAsReadState, setMarkAllAsRead] = useState({
        markRepliesAsRead: false,
        isSubmitting: false,
        error: null
    });
    const [RCPSubmitted, setRCPSubmitted, resetRCPSubmitted] = useStateWithReset(false);

    const {
        dataComments,
        isLoadingInitialData,
        isLoadMore,
        isLoadingMore,
        commentsError,
        mutateComments,
        loadMoreComments
    } = useComments({
        activeSPID: socialProfile?.id,
        mediaId: post?.id
    });

    const handleMarkAllAsRead = async () => {
        setMarkAllAsRead((prevState) => ({
            ...prevState,
            isSubmitting: true,
            error: null
        }));

        try {
            await postRequest(
                injectEndpointUrlParams(COMMENT_MARK_ALL_AS_READ, {
                    SPID: socialProfile?.id,
                    mediaID: post?.id
                })
            );
            mutateComments(
                (prevState) =>
                    prevState.map((page) => ({
                        ...page,
                        data: page.data.map((comment) => ({
                            ...comment,
                            is_read: true
                        })),
                        random_picker_winners: Array.isArray(page?.random_picker_winners)
                            ? page?.random_picker_winners.map((comment) => ({
                                  ...comment,
                                  is_read: true
                              }))
                            : page?.random_picker_winners,
                        summary: {
                            ...page.summary,
                            unread_count: 0
                        }
                    })),
                false
            );
            setMarkAllAsRead((prevState) => ({
                ...prevState,
                isSubmitting: false,
                error: null,
                markRepliesAsRead: true
            }));
        } catch (e) {
            setMarkAllAsRead((prevState) => ({
                ...prevState,
                isSubmitting: false,
                error: true
            }));
        }
    };

    const { isOpen: isOpenExportModal, toggle: toggleExportModal } = useToggle();
    const sendReport = async (email) => {
        const endpoint = injectEndpointUrlParams(COMMENTS_EXPORT, {
            SPID: socialProfile?.id,
            mediaID: post?.id
        });
        await get(endpoint, { email });
    };

    const { isOpen: isOpenRCP, toggle: toggleRCP } = useToggle();

    const refResultsRCP = useRef();
    const resultsRCPinViewPort = useIntersectionObserver(refResultsRCP, '0px');

    if (commentsError) {
        return (
            <InfoBanner>
                <InfoBanner.Icon />
                <InfoBanner.Content
                    title={t('errors:error')}
                    message={t('errors:error_ocurred_fetch_comments')}
                />
            </InfoBanner>
        );
    }

    return (
        <div className="relative pb-12" ref={(ref) => setMediaViewerCommentsRef(ref)}>
            {isLoading || isLoadingInitialData ? (
                <span className="mb-5 block w-1/2 animate-pulse rounded bg-icoGray-300 py-3 dark:bg-icoDarkMode-deepBlack" />
            ) : (
                <>
                    <div ref={refResultsRCP} />
                    <div className="mb-5 flex items-center gap-3">
                        <Icon
                            icon="conversations"
                            className="text-xl text-icoGray-500 dark:text-icoDarkMode-wolf"
                        />
                        <p className="inline flex-1">
                            {t('media_viewer.organic_comments', {
                                count: dataComments?.[0]?.summary?.total_count
                            })}
                            <span className="ml-2 text-icoBlue">
                                ({dataComments[0]?.summary?.unread_count} {t('comment.unread')})
                            </span>
                        </p>
                        <RandomCommentsPicker
                            media={post}
                            socialProfile={socialProfile}
                            isOpen={isOpenRCP}
                            toggle={toggleRCP}
                            network={socialProfile?.social_network_type}
                            onPickCallback={() => {
                                setRCPSubmitted(true);
                                if (!resultsRCPinViewPort) {
                                    setTimeout(() => {
                                        refResultsRCP?.current?.scrollIntoView({
                                            behavior: 'smooth'
                                        });
                                    }, 100);
                                }
                            }}
                            results={dataComments?.[0]?.random_picker_winners}
                            container={mediaViewerCommentsRef}>
                            {({ setReferenceElement }) => (
                                <button
                                    ref={setReferenceElement}
                                    aria-label="export xls"
                                    disabled={dataComments?.[0]?.data?.length === 0} // no comments
                                    data-testid="random-comments-picker-button"
                                    className={clsx(
                                        'flex h-10 w-10 items-center justify-center rounded-full border-2 bg-white disabled:cursor-not-allowed disabled:bg-icoGray-300 dark:bg-icoDarkMode-anthracite',
                                        {
                                            'border-icoBlue text-icoBlue': isOpenRCP,
                                            'border-icoGray-300 text-icoGray-500 dark:border-icoDarkMode-beton dark:text-icoDarkMode-wolf':
                                                !isOpenRCP
                                        }
                                    )}
                                    onClick={toggleRCP}>
                                    <Icon icon="trophy" />
                                </button>
                            )}
                        </RandomCommentsPicker>
                        <button
                            aria-label="export xls"
                            className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-icoGray-300 bg-white text-icoGray-500 dark:border-icoDarkMode-beton dark:bg-icoDarkMode-anthracite dark:text-icoDarkMode-wolf"
                            onClick={toggleExportModal}
                            title={t('commons:export_xls.title_comments')}>
                            <Icon icon="export" />
                        </button>
                    </div>
                </>
            )}
            {RCPSubmitted ? (
                <InfoBanner type="success" className="mb-3">
                    <InfoBanner.Icon icon="check" />
                    <InfoBanner.Content message={t('random_comments_picker.winners_by_email')} />
                    <InfoBanner.Close onClose={resetRCPSubmitted} />
                </InfoBanner>
            ) : null}
            <RandomCommentsPickerResults
                socialProfile={socialProfile}
                mutateComments={mutateComments}
                results={dataComments?.[0]?.random_picker_winners ?? null}
            />
            {markAllAsReadState.error ? (
                <InfoBanner className="mb-4">
                    <InfoBanner.Icon />
                    <InfoBanner.Content
                        title={t('errors:error')}
                        message={t('errors:error_occured_submit_data')}
                    />
                    <InfoBanner.Close
                        onClose={() =>
                            setMarkAllAsRead((prevState) => ({
                                ...prevState,
                                isSubmitting: false,
                                error: null
                            }))
                        }
                    />
                </InfoBanner>
            ) : null}
            <CommentsList
                dataComments={dataComments}
                socialProfile={socialProfile}
                isLoading={isLoading || isLoadingInitialData}
                isLoadingMore={isLoadingMore}
                isLoadMore={isLoadMore}
                handleMarkAllAsRead={handleMarkAllAsRead}
                markAllAsReadState={markAllAsReadState}
                setMarkAllAsRead={setMarkAllAsRead}
                loadMore={loadMoreComments}
                activeFormReply={activeFormReply}
                setActiveFormReply={setActiveFormReply}
                mutateComments={mutateComments}
                repliesClassName="ml-5"
                shouldDisplayIllustration={false} // make sure illustration isn't displayed if no comments
            />
            {!activeFormReply && !isOpenRCP ? (
                <FormProvider {...formMethods}>
                    <CommentForm
                        socialProfile={socialProfile}
                        mediaId={post?.id}
                        mutateComments={mutateComments}
                        comments={dataComments}
                    />
                </FormProvider>
            ) : null}
            <ExportXlsModal
                isOpen={isOpenExportModal}
                title={t('export_xls.title_comments')}
                toggle={toggleExportModal}
                sendReport={sendReport}
            />
        </div>
    );
};

MediaViewerComments.propTypes = {
    post: PropTypes.object,
    socialNetworkId: PropTypes.number,
    socialNetworkType: PropTypes.string,
    isLoading: PropTypes.bool
};

export default MediaViewerComments;
